// src/pages/trading-portal/VolatilityArbitrage/VolatilityArbitrageDashboard.js
// Volatility Arbitrage Engine dashboard overview

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Alert,
    Button,
    Chip,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    TravelExplore as TravelExploreIcon,
    Architecture as ArchitectureIcon,
    Balance as BalanceIcon,
    BarChart as BarChartIcon,
    NavigateNext as NavigateNextIcon,
    AttachMoney as AttachMoneyIcon,
    Warning as WarningIcon,
    Functions as FunctionsIcon,
    Insights as InsightsIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    OpportunityStatusLabels,
    OpportunityStatusColors,
    OpportunityTypeLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

// ─── Navigation items ────────────────────────────────────────────────

const NAV_ITEMS = [
    {
        label: 'Opportunity Detection',
        icon: TravelExploreIcon,
        path: '/trading-portal/volatility-arbitrage/opportunity-detection',
        color: '#3b82f6',
        description: 'Scan for IV vs RV mispricing, VRP, term structure and skew arbitrage',
    },
    {
        label: 'Strategy Construction',
        icon: ArchitectureIcon,
        path: '/trading-portal/volatility-arbitrage/strategy-construction',
        color: '#8b5cf6',
        description: 'Build delta-neutral straddles, gamma scalping, and dispersion trades',
    },
    {
        label: 'Delta Hedging & Risk',
        icon: BalanceIcon,
        path: '/trading-portal/volatility-arbitrage/delta-hedging',
        color: '#ef4444',
        description: 'Monitor delta exposure, manage hedges, and control risk limits',
    },
    {
        label: 'P&L Attribution',
        icon: BarChartIcon,
        path: '/trading-portal/volatility-arbitrage/pnl-attribution',
        color: '#10b981',
        description: 'Greek-level P&L decomposition, performance analytics, and benchmarks',
    },
];

// ─── Sub-components ──────────────────────────────────────────────────

const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}
                    >
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, color: '#94a3b8', fontWeight: 500 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const StatCardSkeleton = () => (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: '14px' }} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" sx={{ fontSize: '0.8rem' }} />
                    <Skeleton variant="text" width="40%" sx={{ fontSize: '1.5rem' }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main component ──────────────────────────────────────────────────

const VolatilityArbitrageDashboard = () => {
    const navigate = useNavigate();

    const [canView, setCanView] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dashboardData, setDashboardData] = useState(null);

    // Permission check
    useEffect(() => {
        const checkPerms = async () => {
            const view = await hasPermission('tr-vae-dashboard', 'view');
            setCanView(view);
        };
        checkPerms();
    }, []);

    // Fetch dashboard data
    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axiosInstance.get('/api/volatility-arbitrage/dashboard');
            const data = response.data?.data || response.data || {};
            setDashboardData({
                active_opportunities: data.active_opportunities || 0,
                active_strategies: data.active_strategies || 0,
                today_pnl: data.today_pnl || 0,
                pending_hedges: data.pending_hedges || 0,
                risk_breaches: data.risk_breaches || 0,
                total_strategies: data.total_strategies || 0,
                recent_opportunities: data.recent_opportunities || [],
            });
        } catch (err) {
            console.error('Failed to fetch volatility arbitrage dashboard:', err);
            setError(err.response?.data?.message || 'Failed to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) {
            fetchDashboard();
        }
    }, [canView, fetchDashboard]);

    // ── Helpers ──────────────────────────────────────────────────────

    const formatCurrency = (value) => {
        if (value == null) return '--';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '--';
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // ── Permission gate ──────────────────────────────────────────────

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ fontFamily }}>
                    You do not have permission to view the Volatility Arbitrage Engine Dashboard.
                </Alert>
            </Box>
        );
    }

    // ── Render ───────────────────────────────────────────────────────

    const todayPnl = dashboardData?.today_pnl ?? 0;
    const pnlColor = todayPnl >= 0 ? '#22c55e' : '#ef4444';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* ── Gradient Header ───────────────────────────────────── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e1e2e 0%, #2d1b4e 100%)',
                    borderRadius: 3,
                    p: 4,
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -40,
                        right: -40,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: `${ACCENT}15`,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -20,
                        left: '30%',
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: `${ACCENT}10`,
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <InsightsIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                    Volatility Arbitrage Engine
                                </Typography>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', mt: 0.25 }}>
                                    Opportunity detection, strategy construction, delta hedging, and P&L attribution
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* ── Error Alert ──────────────────────────────────── */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={fetchDashboard}
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* ── Stat Cards ──────────────────────────────────── */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                    {/* Active Opportunities */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={TravelExploreIcon}
                                title="Active Opportunities"
                                value={dashboardData?.active_opportunities ?? 0}
                                color="#3b82f6"
                            />
                        )}
                    </Grid>

                    {/* Active Strategies */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={ArchitectureIcon}
                                title="Active Strategies"
                                value={dashboardData?.active_strategies ?? 0}
                                color="#8b5cf6"
                            />
                        )}
                    </Grid>

                    {/* Today's P&L */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={AttachMoneyIcon}
                                title="Today's P&L"
                                value={
                                    <Typography
                                        component="span"
                                        variant="h5"
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                            color: pnlColor,
                                        }}
                                    >
                                        {formatCurrency(todayPnl)}
                                    </Typography>
                                }
                                color="#10b981"
                            />
                        )}
                    </Grid>

                    {/* Pending Hedges */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={BalanceIcon}
                                title="Pending Hedges"
                                value={dashboardData?.pending_hedges ?? 0}
                                color="#f59e0b"
                            />
                        )}
                    </Grid>

                    {/* Risk Breaches */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={WarningIcon}
                                title="Risk Breaches"
                                value={dashboardData?.risk_breaches ?? 0}
                                color="#ef4444"
                            />
                        )}
                    </Grid>

                    {/* Total Strategies */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={FunctionsIcon}
                                title="Total Strategies"
                                value={dashboardData?.total_strategies ?? 0}
                                color="#6366f1"
                            />
                        )}
                    </Grid>
                </Grid>

                {/* ── Navigation Cards ────────────────────────────────── */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', mb: 2 }}>
                        Sub-Modules
                    </Typography>
                    <Grid container spacing={2}>
                        {NAV_ITEMS.map((item) => (
                            <Grid item xs={12} sm={6} md={3} key={item.path}>
                                <Card
                                    sx={{
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: 'none',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: item.color,
                                            boxShadow: `0 4px 20px ${item.color}20`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                    onClick={() => navigate(item.path)}
                                >
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: '14px',
                                                        bgcolor: `${item.color}15`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <item.icon sx={{ color: item.color, fontSize: 24 }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.78rem', fontWeight: 500 }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <NavigateNextIcon sx={{ color: '#cbd5e1', fontSize: 22 }} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* ── Recent Opportunities Table ──────────────────────── */}
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TravelExploreIcon sx={{ color: '#3b82f6', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Recent Opportunities
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/volatility-arbitrage/opportunity-detection')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                <Box sx={{ px: 1, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 1.5 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
                                                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
                                                </Box>
                                            </Box>
                                        ))
                                    ) : !dashboardData?.recent_opportunities?.length ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <TravelExploreIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No recent opportunities
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Opportunities will appear here once detected
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Symbol</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Type</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }} align="right">IV</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }} align="right">Predicted RV</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }} align="right">Z-Score</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Status</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Detected At</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dashboardData.recent_opportunities.slice(0, 10).map((opp, index) => (
                                                        <TableRow key={opp.id || index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {opp.symbol || '--'}
                                                            </TableCell>
                                                            <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <Chip
                                                                    label={OpportunityTypeLabels[opp.opportunity_type] || opp.opportunity_type || '--'}
                                                                    size="small"
                                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {opp.iv != null ? `${parseFloat(opp.iv).toFixed(2)}%` : '--'}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {opp.predicted_rv != null ? `${parseFloat(opp.predicted_rv).toFixed(2)}%` : '--'}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {opp.z_score != null ? parseFloat(opp.z_score).toFixed(2) : '--'}
                                                            </TableCell>
                                                            <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <Chip
                                                                    label={OpportunityStatusLabels[opp.status] || opp.status || '--'}
                                                                    size="small"
                                                                    color={OpportunityStatusColors[opp.status] || 'default'}
                                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {formatDateTime(opp.detected_at)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default VolatilityArbitrageDashboard;
